import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { post } from "@/utils/api-handler";
import { useProtectedMutation } from "@/hooks/use-protected-mutation";
import { useCommandMessages } from "@/hooks/use-command-messages";
import { useCommandSessions } from "@/hooks/use-command-sessions";
import { useFileAttachmentQueue } from "@/hooks/use-file-attachment-queue";
import type { CommandSession } from "@/types/command-agent";
import { ExtendedChatMessage } from "@/components/aios-command/chat/chat-types";
import { useMergedMessages } from "@/components/aios-command/chat/use-merged-messages";
import { useStreamReconnect } from "@/components/aios-command/chat/use-stream-reconnect";
import { useConversationScroll } from "@/components/aios-command/chat/use-conversation-scroll";
import { useSendMessage } from "@/components/aios-command/chat/use-send-message";

/**
 * Owns the message-thread state machine for the global Command panel: fetches
 * the active agent's messages/sessions, merges them with local optimistic +
 * streaming state, and wires the shared send/reconnect infra from the
 * command-team chat (`use-send-message`, `use-stream-reconnect`,
 * `use-merged-messages`, `use-conversation-scroll`). Mirrors the plumbing in
 * `chat-area.tsx`, minus the URL-driven session sync (this panel is the only
 * writer of its own session id) and the preset/model/attachment furniture.
 */
export function useCommandPanelThread(agentId: string | null) {
    const queryClient = useQueryClient();

    const [messages, setMessages] = useState<ExtendedChatMessage[]>([]);
    const [inputValue, setInputValue] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
    const [, setStreamingPhase] = useState<"working" | "responding">("working");
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

    const isTypingRef = useRef(isTyping);
    isTypingRef.current = isTyping;
    const streamingMessageIdRef = useRef(streamingMessageId);
    streamingMessageIdRef.current = streamingMessageId;
    const fetchedMessagesRef = useRef<ExtendedChatMessage[]>([]);
    const conversationRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const pendingMessagesRef = useRef<ExtendedChatMessage[]>([]);
    const streamAbortRef = useRef<Set<AbortController>>(new Set());
    const streamCancelledRef = useRef(false);
    // TODO: type this — matches the untyped `stickyContextRef` shape used by use-conversation-scroll/use-send-message
    const stickyContextRef = useRef<any>(null);

    const {
        allMessages: fetchedMessages,
        isLoadingMessages,
        isFetchingMessages,
        isFetchingNextPage,
        hasNextPage,
        loadMoreRef,
        canListMessages,
        isMessagesError,
        messagesError,
    } = useCommandMessages(agentId, currentSessionId);
    fetchedMessagesRef.current = fetchedMessages as ExtendedChatMessage[];

    const {
        allSessions,
        isLoadingSessions,
        isFetchingNextPage: isFetchingNextSessionsPage,
        hasNextPage: hasNextSessionsPage,
        loadMoreRef: sessionsLoadMoreRef,
        canListSessions,
        updateSession,
        isUpdatingSession,
        deleteSession,
        isDeletingSession,
    } = useCommandSessions(agentId);

    const attachmentQueue = useFileAttachmentQueue();
    const sessionStreamActive = useMemo(() => {
        if (!currentSessionId) return false;
        const row = allSessions.find((s) => s.id === currentSessionId);
        return row?.status?.toUpperCase() === "GENERATING";
    }, [allSessions, currentSessionId]);

    const generatingAssistantId = useMemo(() => {
        for (let i = fetchedMessages.length - 1; i >= 0; i--) {
            const m = fetchedMessages[i];
            if (m.role === "assistant" && (m as { status?: string }).status === "GENERATING") return m.id;
        }
        return null;
    }, [fetchedMessages]);

    useMergedMessages({
        fetchedMessages,
        isLoadingMessages,
        isFetchingMessages,
        sessionId: currentSessionId,
        isTyping,
        streamingMessageId,
        pendingMessagesRef,
        setMessages,
    });

    const streamSupervisor = useStreamReconnect({
        agentId,
        sessionId: currentSessionId,
        sessionStreamActive,
        generatingAssistantId,
        isLoadingMessages,
        fetchedMessagesRef,
        isTypingRef,
        streamingMessageIdRef,
        isTyping,
        streamingMessageId,
        streamAbortRef,
        setMessages,
        setIsTyping,
        setStreamingMessageId,
        setStreamingPhase,
        queryClient,
    });

    const { paginatedNewIds, reset: resetScroll } = useConversationScroll({
        messages,
        isFetchingNextPage,
        isLoadingMessages,
        conversationRef,
        stickyContextRef,
    });

    const createSessionMutation = useProtectedMutation<CommandSession>({
        mutationFn: async () => {
            const response = await post(`/api/command-team/agents/${agentId}/sessions`, {});
            return response.data as CommandSession;
        },
        requiredPermissions: ["CREATE_COMMAND_SESSION"],
        onSuccess: (data) => {
            if (!data?.id) return;
            setCurrentSessionId(data.id);
            const now = new Date().toISOString();
            const newSession = {
                ...data,
                title: data.title ?? "",
                status: data.status ?? "active",
                pinned: data.pinned ?? false,
                message_count: data.message_count ?? 0,
                total_tokens_used: data.total_tokens_used ?? 0,
                created_at: data.created_at ?? now,
                updated_at: data.updated_at ?? now,
            };
            queryClient.setQueryData(
                ["command-sessions", agentId],
                // TODO: type this — matches the cache shape chat-area.tsx's equivalent createSessionMutation writes
                (old: any) => {
                    if (!old?.pages?.length) {
                        return { pages: [{ items: [newSession], item_count: 1, page_count: 1 }], pageParams: [1] };
                    }
                    const updatedPages = [...old.pages];
                    updatedPages[0] = {
                        ...updatedPages[0],
                        items: [newSession, ...(updatedPages[0].items ?? [])],
                        item_count: (updatedPages[0].item_count ?? 0) + 1,
                    };
                    return { ...old, pages: updatedPages };
                }
            );
        },
        // TODO: type this — mirrors the loosely-typed axios error shape used across use-send-message.ts/chat-area.tsx
        onError: (error: any) => {
            const data = error?.response?.data;
            const description =
                (typeof data?.message === "string" && data.message) ||
                (typeof data?.detail === "string" && data.detail) ||
                "We couldn't start a new chat. Please try again.";
            toast.error("Failed to create session", { description });
            setIsTyping(false);
        },
    });

    const { sendMessage, handleStopGeneration, isStopping } = useSendMessage({
        agentId,
        currentSessionId,
        isTyping,
        streamingMessageId,
        attachedFiles: attachmentQueue.attachedFiles,
        createSessionMutation,
        clearAttachments: attachmentQueue.clear,
        setInputValue,
        setCurrentSessionId,
        setMessages,
        setIsTyping,
        setStreamingMessageId,
        setStreamingPhase,
        streamCancelledRef,
        streamAbortRef,
        pendingMessagesRef,
        stickyContextRef,
        inputRef,
        queryClient,
        streamSupervisor,
    });

    const handleSubmit = useCallback(
        (e?: { preventDefault?: () => void }) => {
            e?.preventDefault?.();
            if ((isTyping || streamingMessageId) && agentId && currentSessionId) {
                handleStopGeneration();
                return;
            }
            sendMessage(inputValue);
        },
        [inputValue, isTyping, streamingMessageId, agentId, currentSessionId, handleStopGeneration, sendMessage]
    );

    const retryMessages = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ["command-messages", agentId, currentSessionId] });
    }, [queryClient, agentId, currentSessionId]);

    /** Stops UI updates for whatever's currently streaming and clears the thread, without touching the session row itself. */
    const resetThread = useCallback(() => {
        streamCancelledRef.current = true;
        setMessages([]);
        pendingMessagesRef.current = [];
        setIsTyping(false);
        setStreamingMessageId(null);
        resetScroll();
    }, [resetScroll]);

    const startNewChat = useCallback(() => {
        resetThread();
        setCurrentSessionId(null);
        setInputValue("");
    }, [resetThread]);

    const selectSession = useCallback(
        (sessionId: string) => {
            if (sessionId === currentSessionId) return;
            resetThread();
            setCurrentSessionId(sessionId);
        },
        [currentSessionId, resetThread]
    );

    // A session belongs to exactly one agent — if the globally active agent flips
    // while the panel has an open thread, start fresh rather than reusing a session
    // id that would 404 against the new agent. Stop UI updates for the prior stream
    // (cancelledRef) but let its HTTP request finish server-side, same as chat-area
    // does on a session switch.
    const prevAgentIdRef = useRef<string | null>(agentId);
    useEffect(() => {
        if (prevAgentIdRef.current === agentId) return;
        prevAgentIdRef.current = agentId;
        resetThread();
        setCurrentSessionId(null);
    }, [agentId, resetThread]);

    return {
        messages,
        inputValue,
        setInputValue,
        isTyping,
        streamingMessageId,
        sendMessage,
        handleStopGeneration,
        isStopping,
        handleSubmit,
        isLoadingMessages,
        isMessagesError,
        messagesError,
        retryMessages,
        canListMessages,
        paginatedNewIds,
        conversationRef,
        inputRef,
        stickyContextRef,
        loadMoreRef,
        hasNextPage,
        currentSessionId,
        allSessions,
        isLoadingSessions,
        canListSessions,
        hasNextSessionsPage,
        isFetchingNextSessionsPage,
        sessionsLoadMoreRef,
        updateSession,
        isUpdatingSession,
        deleteSession,
        isDeletingSession,
        startNewChat,
        selectSession,
        ...attachmentQueue,
    };
}
